import { useState, useEffect, useCallback } from 'react';
import { Shield, LogIn, LogOut, Activity, Users, UserCog, BarChart3, Eye, Search, ChevronLeft, ChevronRight, AlertTriangle, Lock, Clock, Globe, Filter, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// --- Login Page ---
const SuperAdminLogin = ({ onLogin }) => {
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/superadmin/login`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 15000;
        xhr.onload = () => {
          try {
            if (xhr.responseText.trim().startsWith('<')) {
              reject(new Error('Server sedang tidak tersedia. Coba lagi dalam beberapa detik.'));
              return;
            }
            const result = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(result);
            } else {
              reject(new Error(result.detail || 'Login failed'));
            }
          } catch {
            reject(new Error('Server sedang tidak tersedia. Coba lagi.'));
          }
        };
        xhr.onerror = () => reject(new Error('Gagal terhubung ke server.'));
        xhr.ontimeout = () => reject(new Error('Request timeout. Coba lagi.'));
        xhr.send(JSON.stringify({ id, password }));
      });
      localStorage.setItem('sa_token', data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Super Admin</h1>
          <p className="text-slate-500 text-sm mt-1 dark:text-zinc-400">Nerava Platform Control</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1.5 dark:text-zinc-400">ID</label>
            <input
              data-testid="sa-login-id"
              type="text"
              value={id}
              onChange={e => setId(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              placeholder="Enter your ID"
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1.5 dark:text-zinc-400">Password</label>
            <input
              data-testid="sa-login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              placeholder="Enter password"
              required
            />
          </div>
          {error && (
            <div data-testid="sa-login-error" className="flex items-center gap-2 text-red-400 text-xs bg-red-950/50 border border-red-900 rounded-lg px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
          <button
            data-testid="sa-login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? 'Authenticating...' : 'Access Control Panel'}
          </button>
        </form>
        <p className="text-slate-700 text-[10px] text-center mt-6 dark:text-zinc-200">Restricted Access. All activity is logged.</p>
      </div>
    </div>
  );
};

// --- Stat Card ---
const StatCard = ({ label, value, icon: Icon, color = 'blue' }) => {
  const colors = {
    blue: 'from-blue-600 to-blue-700',
    green: 'from-emerald-600 to-emerald-700',
    purple: 'from-violet-600 to-violet-700',
    amber: 'from-amber-600 to-amber-700',
    red: 'from-red-600 to-red-700',
    slate: 'from-slate-600 to-slate-700',
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-500 text-xs font-medium dark:text-zinc-400">{label}</span>
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value ?? '-'}</p>
    </div>
  );
};

// --- Dashboard Page ---
const SuperAdminDashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logFilter, setLogFilter] = useState('');
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', name: '' });
  const [adminMsg, setAdminMsg] = useState('');
  const [adminErr, setAdminErr] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const apiFetch = useCallback(async (path) => {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { onLogout(); return null; }
    const text = await res.text();
    if (!res.ok) throw new Error('API error');
    try { return JSON.parse(text); } catch { return null; }
  }, [token, onLogout]);

  const loadStats = useCallback(async () => {
    try {
      const data = await apiFetch('/api/superadmin/stats');
      if (data) setStats(data.stats);
    } catch (err) { console.error('Failed to load stats:', err); }
  }, [apiFetch]);

  const loadLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: logsPage, limit: 20 });
      if (logFilter) params.set('action', logFilter);
      const data = await apiFetch(`/api/superadmin/audit-logs?${params}`);
      if (data) { setLogs(data.logs); setLogsTotal(data.total); }
    } catch (err) { console.error('Failed to load logs:', err); }
  }, [apiFetch, logsPage, logFilter]);

  const loadAdmins = useCallback(async () => {
    try {
      const data = await apiFetch('/api/superadmin/admins');
      if (data) setAdmins(data.admins);
    } catch (err) { console.error('Failed to load admins:', err); }
  }, [apiFetch]);

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: usersPage, limit: 20 });
      if (userSearch) params.set('search', userSearch);
      const data = await apiFetch(`/api/superadmin/users?${params}`);
      if (data) { setUsers(data.users); setUsersTotal(data.total); }
    } catch (err) { console.error('Failed to load users:', err); }
  }, [apiFetch, usersPage, userSearch]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);
  useEffect(() => { if (tab === 'admins') loadAdmins(); }, [tab, loadAdmins]);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, loadUsers]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/superadmin/logout`, { method: 'POST', headers });
    } catch (err) { console.error('Logout request failed:', err); }
    localStorage.removeItem('sa_token');
    onLogout();
  };

  const handleRoleChange = async (email, newRole) => {
    if (!window.confirm(`Ubah role ${email} menjadi "${newRole}"?`)) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/superadmin/admins/role`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_email: email, new_role: newRole }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) throw new Error(data.detail || 'Gagal update role');
      loadAdmins();
      loadUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const apiSend = useCallback(async (path, method, body) => {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { data = { detail: text }; }
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Gagal memproses permintaan');
    return data;
  }, [token]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setAdminErr(''); setAdminMsg(''); setLoading(true);
    try {
      await apiSend('/api/superadmin/admins', 'POST', {
        username: newAdmin.username.trim().toLowerCase(),
        password: newAdmin.password,
        name: newAdmin.name || null,
      });
      setAdminMsg(`Admin "${newAdmin.username.trim().toLowerCase()}" berhasil dibuat`);
      setNewAdmin({ username: '', password: '', name: '' });
      loadAdmins();
    } catch (err) {
      setAdminErr(err.message);
    } finally { setLoading(false); }
  };

  const handleToggleAdmin = async (admin) => {
    const disabling = !admin.is_admin_disabled;
    if (!window.confirm(`${disabling ? 'Nonaktifkan' : 'Aktifkan kembali'} admin "${admin.admin_username}"?`)) return;
    setAdminErr(''); setAdminMsg(''); setLoading(true);
    try {
      await apiSend(`/api/superadmin/admins/${admin.id}/status`, 'PUT', { disabled: disabling });
      setAdminMsg(`Admin "${admin.admin_username}" ${disabling ? 'dinonaktifkan' : 'diaktifkan'}`);
      loadAdmins();
    } catch (err) { setAdminErr(err.message); } finally { setLoading(false); }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Hapus permanen admin "${admin.admin_username}"?`)) return;
    setAdminErr(''); setAdminMsg(''); setLoading(true);
    try {
      await apiSend(`/api/superadmin/admins/${admin.id}`, 'DELETE');
      setAdminMsg(`Admin "${admin.admin_username}" dihapus`);
      loadAdmins();
    } catch (err) { setAdminErr(err.message); } finally { setLoading(false); }
  };

  const handleResetAdminPassword = async (admin) => {
    const pw = window.prompt(`Password baru untuk "${admin.admin_username}" (minimal 8 karakter):`);
    if (!pw) return;
    setAdminErr(''); setAdminMsg(''); setLoading(true);
    try {
      await apiSend(`/api/superadmin/admins/${admin.id}/password`, 'PUT', { password: pw });
      setAdminMsg(`Password "${admin.admin_username}" diperbarui`);
    } catch (err) { setAdminErr(err.message); } finally { setLoading(false); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'logs', label: 'Audit Logs', icon: Activity },
    { id: 'admins', label: 'Admins', icon: UserCog },
    { id: 'users', label: 'Users', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>Super Admin</h1>
              <p className="text-[10px] text-slate-500 dark:text-zinc-400">Nerava Control Panel</p>
            </div>
          </div>
          <button
            data-testid="sa-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-red-400 border border-slate-800 hover:border-red-800 rounded-lg transition-colors dark:text-zinc-400"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      {/* Tab nav */}
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto scrollbar-hide">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`sa-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? 'border-red-500 text-red-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              } dark:text-zinc-400`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview */}
        {tab === 'overview' && stats && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-300">Platform Overview</h2>
              <button onClick={loadStats} className="p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-800 transition-colors dark:text-zinc-400">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Total Users" value={stats.total_users} icon={Users} color="blue" />
              <StatCard label="Admins" value={stats.total_admins} icon={UserCog} color="purple" />
              <StatCard label="Orders" value={stats.total_orders} icon={BarChart3} color="green" />
              <StatCard label="Conversations" value={stats.total_conversations} icon={Activity} color="amber" />
              <StatCard label="Chatbots" value={stats.total_chatbots} icon={Globe} color="slate" />
              <StatCard label="Audit Logs" value={stats.total_audit_logs} icon={Eye} color="blue" />
              <StatCard label="SA Logins" value={stats.superadmin_logins} icon={LogIn} color="green" />
              <StatCard label="Failed Logins" value={stats.failed_login_attempts} icon={AlertTriangle} color="red" />
            </div>
          </div>
        )}

        {/* Audit Logs */}
        {tab === 'logs' && (
          <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <h2 className="text-sm font-semibold text-slate-300">Audit Logs</h2>
              <div className="ml-auto flex items-center gap-2">
                <div className="relative">
                  <Filter className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-400" />
                  <input
                    data-testid="sa-log-filter"
                    type="text"
                    value={logFilter}
                    onChange={e => { setLogFilter(e.target.value); setLogsPage(1); }}
                    placeholder="Filter action..."
                    className="pl-8 pr-3 py-1.5 text-xs bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 outline-none focus:ring-1 focus:ring-red-500 w-40"
                  />
                </div>
                <button onClick={loadLogs} className="p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-800 dark:text-zinc-400">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 dark:text-zinc-400">
                      <th className="text-left px-4 py-2.5 font-medium">Timestamp</th>
                      <th className="text-left px-4 py-2.5 font-medium">Admin</th>
                      <th className="text-left px-4 py-2.5 font-medium">Action</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden sm:table-cell">Detail</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {logs.map((log, i) => (
                      <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap dark:text-zinc-400">
                          <Clock className="w-3 h-3 inline mr-1 opacity-50" />
                          {log.timestamp?.replace('T', ' ').substring(0, 19)}
                        </td>
                        <td className="px-4 py-2.5 text-white font-medium">{log.admin_id}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            log.action?.includes('login') ? 'bg-green-900/50 text-green-400 border border-green-800' :
                            log.action?.includes('failed') ? 'bg-red-900/50 text-red-400 border border-red-800' :
                            log.action?.includes('role') ? 'bg-amber-900/50 text-amber-400 border border-amber-800' :
                            log.action?.includes('logout') ? 'bg-slate-800 text-slate-500 border border-slate-700' :
                            'bg-blue-900/50 text-blue-400 border border-blue-800'
                          } dark:text-zinc-400`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell dark:text-zinc-400">{log.detail || '-'}</td>
                        <td className="px-4 py-2.5 text-slate-600 font-mono hidden md:table-cell">{log.ip_address}</td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-600">No logs found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {logsTotal > 20 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-800">
                  <span className="text-[10px] text-slate-500 dark:text-zinc-400">{logsTotal} total</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setLogsPage(p => Math.max(1, p-1))} disabled={logsPage <= 1} className="p-1 rounded hover:bg-slate-800 disabled:opacity-30 text-slate-500 dark:text-zinc-400"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-xs text-slate-500 px-2 dark:text-zinc-400">Page {logsPage}</span>
                    <button onClick={() => setLogsPage(p => p+1)} disabled={logs.length < 20} className="p-1 rounded hover:bg-slate-800 disabled:opacity-30 text-slate-500 dark:text-zinc-400"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Admins */}
        {tab === 'admins' && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-slate-300">Admin Management</h2>

            {/* Create admin */}
            <form onSubmit={handleCreateAdmin} data-testid="sa-create-admin-form" className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-slate-300 mb-3 flex items-center gap-1.5">
                <UserCog className="w-3.5 h-3.5" /> Buat Admin Baru (login pakai username)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input
                  data-testid="sa-new-admin-username"
                  value={newAdmin.username}
                  onChange={e => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  placeholder="username (min 4)"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  className="px-3 py-2 text-xs bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 outline-none focus:ring-1 focus:ring-red-500"
                />
                <input
                  data-testid="sa-new-admin-password"
                  type="password"
                  value={newAdmin.password}
                  onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder="password (min 8)"
                  required
                  className="px-3 py-2 text-xs bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 outline-none focus:ring-1 focus:ring-red-500"
                />
                <input
                  data-testid="sa-new-admin-name"
                  value={newAdmin.name}
                  onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  placeholder="nama (opsional)"
                  className="px-3 py-2 text-xs bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 outline-none focus:ring-1 focus:ring-red-500"
                />
                <button
                  data-testid="sa-create-admin-submit"
                  type="submit"
                  disabled={loading}
                  className="px-3 py-2 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  Buat Admin
                </button>
              </div>
              {adminErr && <p data-testid="sa-admin-error" className="text-[11px] text-red-400 mt-2">{adminErr}</p>}
              {adminMsg && <p data-testid="sa-admin-success" className="text-[11px] text-emerald-400 mt-2">{adminMsg}</p>}
              <p className="text-[10px] text-slate-500 mt-2 dark:text-zinc-400">
                Admin baru hanya bisa masuk ke /nrv-panel dan tidak dihitung sebagai pengguna aplikasi.
              </p>
            </form>

            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 dark:text-zinc-400">
                      <th className="text-left px-4 py-2.5 font-medium">Username</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden sm:table-cell">Nama</th>
                      <th className="text-left px-4 py-2.5 font-medium">Tipe</th>
                      <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Login Terakhir</th>
                      <th className="text-left px-4 py-2.5 font-medium">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {admins.map((a, i) => (
                      <tr key={a.id || i} data-testid={`sa-admin-row-${a.admin_username || i}`} className="hover:bg-slate-800/30">
                        <td className="px-4 py-2.5 text-white font-medium">
                          {a.admin_username || <span className="text-amber-400">belum ada username</span>}
                          {!a.is_platform_admin && <span className="block text-[10px] text-slate-500 dark:text-zinc-400">{a.email}</span>}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell dark:text-zinc-400">{a.name || '-'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            a.role === 'superadmin' ? 'bg-red-900/50 text-red-400 border border-red-800'
                              : a.is_platform_admin ? 'bg-violet-900/50 text-violet-400 border border-violet-800'
                              : 'bg-blue-900/50 text-blue-400 border border-blue-800'
                          }`}>{a.role === 'superadmin' ? 'superadmin' : a.is_platform_admin ? 'admin panel' : 'admin + pengguna'}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            a.is_admin_disabled ? 'bg-slate-800 text-slate-400 border border-slate-700' : 'bg-emerald-900/50 text-emerald-400 border border-emerald-800'
                          }`}>{a.is_admin_disabled ? 'nonaktif' : 'aktif'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell dark:text-zinc-400">
                          {a.last_admin_login ? a.last_admin_login.replace('T', ' ').substring(0, 16) : '-'}
                        </td>
                        <td className="px-4 py-2.5">
                          {a.role === 'superadmin' ? (
                            <Lock className="w-3 h-3 text-slate-700 dark:text-zinc-200" />
                          ) : (
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                data-testid={`sa-admin-toggle-${a.admin_username || i}`}
                                onClick={() => handleToggleAdmin(a)}
                                disabled={loading}
                                className="text-[10px] text-amber-400 hover:text-amber-300 font-medium"
                              >
                                {a.is_admin_disabled ? 'Aktifkan' : 'Nonaktifkan'}
                              </button>
                              {a.is_platform_admin ? (
                                <>
                                  <button
                                    data-testid={`sa-admin-password-${a.admin_username || i}`}
                                    onClick={() => handleResetAdminPassword(a)}
                                    disabled={loading}
                                    className="text-[10px] text-blue-400 hover:text-blue-300 font-medium"
                                  >
                                    Reset Password
                                  </button>
                                  <button
                                    data-testid={`sa-admin-delete-${a.admin_username || i}`}
                                    onClick={() => handleDeleteAdmin(a)}
                                    disabled={loading}
                                    className="text-[10px] text-red-400 hover:text-red-300 font-medium"
                                  >
                                    Hapus
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleRoleChange(a.email, 'user')}
                                  disabled={loading}
                                  className="text-[10px] text-red-400 hover:text-red-300 font-medium"
                                >
                                  Demote to User
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {admins.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-600">No admins found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {tab === 'users' && (
          <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <h2 className="text-sm font-semibold text-slate-300">All Users</h2>
              <div className="ml-auto flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-400" />
                  <input
                    data-testid="sa-user-search"
                    type="text"
                    value={userSearch}
                    onChange={e => { setUserSearch(e.target.value); setUsersPage(1); }}
                    placeholder="Search email/name..."
                    className="pl-8 pr-3 py-1.5 text-xs bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 outline-none focus:ring-1 focus:ring-red-500 w-48"
                  />
                </div>
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 dark:text-zinc-400">
                      <th className="text-left px-4 py-2.5 font-medium">Email</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden sm:table-cell">Name</th>
                      <th className="text-left px-4 py-2.5 font-medium">Role</th>
                      <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Plan</th>
                      <th className="text-left px-4 py-2.5 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {users.map((u, i) => (
                      <tr key={i} className="hover:bg-slate-800/30">
                        <td className="px-4 py-2.5 text-white">{u.email}</td>
                        <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell dark:text-zinc-400">{u.name || '-'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            u.role === 'superadmin' ? 'bg-red-900/50 text-red-400 border border-red-800' :
                            u.role === 'admin' ? 'bg-violet-900/50 text-violet-400 border border-violet-800' :
                            'bg-slate-800 text-slate-500 border border-slate-700'
                          } dark:text-zinc-400`}>{u.role || 'user'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell dark:text-zinc-400">{u.plan || 'free'}</td>
                        <td className="px-4 py-2.5">
                          {u.role === 'superadmin' ? (
                            <Lock className="w-3 h-3 text-slate-700 dark:text-zinc-200" />
                          ) : u.role === 'admin' ? (
                            <button onClick={() => handleRoleChange(u.email, 'user')} disabled={loading} className="text-[10px] text-red-400 hover:text-red-300 font-medium">Demote</button>
                          ) : (
                            <button onClick={() => handleRoleChange(u.email, 'admin')} disabled={loading} className="text-[10px] text-emerald-400 hover:text-emerald-300 font-medium">Promote</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-600">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {usersTotal > 20 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-800">
                  <span className="text-[10px] text-slate-500 dark:text-zinc-400">{usersTotal} total</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setUsersPage(p => Math.max(1, p-1))} disabled={usersPage <= 1} className="p-1 rounded hover:bg-slate-800 disabled:opacity-30 text-slate-500 dark:text-zinc-400"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-xs text-slate-500 px-2 dark:text-zinc-400">Page {usersPage}</span>
                    <button onClick={() => setUsersPage(p => p+1)} disabled={users.length < 20} className="p-1 rounded hover:bg-slate-800 disabled:opacity-30 text-slate-500 dark:text-zinc-400"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// --- Main Component ---
export default function SuperAdminPage() {
  const [token, setToken] = useState(localStorage.getItem('sa_token') || '');
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    fetch(`${API}/api/superadmin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) { setVerified(true); } else { localStorage.removeItem('sa_token'); setToken(''); } })
      .catch(() => { localStorage.removeItem('sa_token'); setToken(''); })
      .finally(() => setChecking(false));
  }, [token]);

  if (checking) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>;

  if (!token || !verified) return <SuperAdminLogin onLogin={(t) => { setToken(t); setVerified(true); }} />;

  return <SuperAdminDashboard token={token} onLogout={() => { setToken(''); setVerified(false); }} />;
}
